// frontend/src/components/admin/GoogleMapsCard.jsx
import { useState, useEffect } from 'react';
import adminService from '../../services/adminService';

const GoogleMapsCard = () => {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const response = await adminService.getGoogleMapsUsage();
      if (response.success) {
        setUsage(response.data);
      }
    } catch (error) {
      console.error('Fetch usage error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    if (!usage) return;
    
    try {
      setUpdating(true);
      const newSettings = {
        ...usage.settings,
        enabled: !usage.settings.enabled
      };
      
      const response = await adminService.updateGoogleMapsSettings(newSettings);
      
      if (response.success) {
        setUsage(prev => ({
          ...prev,
          settings: response.data
        }));
        alert(`✅ Google Maps API ${response.data.enabled ? 'идэвхжлээ' : 'идэвхгүй болсон'}`);
      }
    } catch (error) {
      console.error('Toggle error:', error);
      alert('❌ Тохиргоо өөрчлөхөд алдаа гарлаа');
    } finally {
      setUpdating(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Өнөөдрийн хэрэглээг reset хийх үү?')) return;
    
    try {
      setUpdating(true);
      const response = await adminService.resetGoogleMapsUsage();
      
      if (response.success) {
        alert('✅ Хэрэглээ reset хийгдлээ');
        fetchUsage();
      }
    } catch (error) {
      console.error('Reset error:', error);
      alert('❌ Reset хийхэд алдаа гарлаа');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-card">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="admin-card">
        <p className="text-gray-600">Google Maps хэрэглээ ачаалагдсангүй</p>
      </div>
    );
  }

  const { today, settings } = usage;
  const isNearLimit = today.percentage >= 80;
  const isOverLimit = today.percentage >= 100;

  return (
    <div className="admin-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="admin-card-header flex items-center gap-2">
          <span>🗺️</span>
          Google Maps API
        </h3>
        
        {/* Enable/Disable Toggle */}
        <button
          onClick={handleToggle}
          disabled={updating}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
            settings.enabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-red-100 text-red-700 hover:bg-red-200'
          } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {updating ? '...' : settings.enabled ? '✅ Идэвхтэй' : '❌ Идэвхгүй'}
        </button>
      </div>

      {/* Usage Stats */}
      <div className="space-y-4">
        {/* Today's Usage */}
        <div className={`p-4 rounded-xl border-2 ${
          isOverLimit 
            ? 'bg-red-50 border-red-200' 
            : isNearLimit 
            ? 'bg-yellow-50 border-yellow-200' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Өнөөдрийн хэрэглээ</span>
            <span className={`text-lg font-bold ${
              isOverLimit ? 'text-red-700' : isNearLimit ? 'text-yellow-700' : 'text-blue-700'
            }`}>
              {today.count} / {today.limit}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                isOverLimit 
                  ? 'bg-red-500' 
                  : isNearLimit 
                  ? 'bg-yellow-500' 
                  : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(today.percentage, 100)}%` }}
            ></div>
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-600">
              {today.remaining > 0 ? `${today.remaining} үлдсэн` : 'Хязгаарт хүрсэн'}
            </span>
            <span className="text-xs font-semibold text-gray-700">{today.percentage}%</span>
          </div>
        </div>

        {/* Warning */}
        {isNearLimit && !isOverLimit && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <span className="text-yellow-600">⚠️</span>
            <p className="text-xs text-yellow-700">
              Өдрийн хязгаарт ойртож байна. Хэрэглээгээ хянаарай.
            </p>
          </div>
        )}

        {isOverLimit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <span className="text-red-600">🚫</span>
            <p className="text-xs text-red-700">
              Өдрийн хязгаарт хүрсэн. Google Maps автоматаар идэвхгүй болсон.
            </p>
          </div>
        )}

        {/* Last 7 Days Chart */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Сүүлийн 7 хоног</h4>
          <div className="space-y-2">
            {usage.last7Days.slice(-7).map((day, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 w-20 flex-shrink-0">
                  {new Date(day.date).toLocaleDateString('mn-MN', { month: 'short', day: 'numeric' })}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${Math.min((day.count / settings.dailyLimit) * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs font-semibold text-gray-700 w-12 text-right">
                  {day.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-gray-200 flex gap-2">
          <button
            onClick={fetchUsage}
            disabled={updating}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition"
          >
            🔄 Шинэчлэх
          </button>
          <button
            onClick={handleReset}
            disabled={updating}
            className="flex-1 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium transition"
          >
            ↺ Reset
          </button>
        </div>

        {/* Settings Info */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Хязгаарууд</h4>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="p-2 bg-gray-50 rounded text-center">
              <p className="text-gray-600">Өдөр</p>
              <p className="font-bold text-gray-900">{settings.dailyLimit}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded text-center">
              <p className="text-gray-600">Цаг</p>
              <p className="font-bold text-gray-900">{settings.hourlyLimit}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded text-center">
              <p className="text-gray-600">Минут</p>
              <p className="font-bold text-gray-900">{settings.minuteLimit}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapsCard;